// Site-wide animation runtime.
//
// Two effects are wired here so every template — not just the homepage —
// gets the same treatment:
//
//   1. Reveal-on-scroll for `.reveal` elements (opacity + translateY fade).
//      Siblings sharing a parent are auto-staggered by 80ms each.
//   2. Counter tween for `[data-count]` numbers (eased ease-out cubic).
//
// `prefers-reduced-motion: reduce` short-circuits both — elements are
// flipped to their final state without animating.

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

let revealObserver = null
let counterObserver = null

const setupReveal = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
  if (revealObserver) revealObserver.disconnect()

  const els = document.querySelectorAll('.reveal:not(.in)')

  // Stagger siblings: each `.reveal` after the first under a shared parent
  // gets a transition-delay so a section's cards cascade rather than pop.
  const counters = new WeakMap()
  els.forEach(el => {
    const parent = el.parentElement
    if (!parent) return
    const idx = counters.get(parent) || 0
    counters.set(parent, idx + 1)
    if (idx > 0 && !el.style.transitionDelay) {
      el.style.transitionDelay = `${Math.min(idx * 80, 480)}ms`
    }
  })

  if (prefersReducedMotion()) {
    els.forEach(el => el.classList.add('in'))
    return
  }

  revealObserver = new IntersectionObserver(
    entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in')
          revealObserver.unobserve(e.target)
        }
      })
    },
    { threshold: 0.1, rootMargin: '0px 0px -60px 0px' },
  )
  els.forEach(el => revealObserver.observe(el))
}

const setupCounters = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
  if (counterObserver) counterObserver.disconnect()

  const els = document.querySelectorAll('[data-count]:not([data-count-done])')

  if (prefersReducedMotion()) {
    els.forEach(el => {
      el.textContent = el.dataset.count
      el.dataset.countDone = '1'
    })
    return
  }

  counterObserver = new IntersectionObserver(
    entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return
        const el = e.target
        const target = parseInt(el.dataset.count, 10)
        if (Number.isNaN(target)) return
        const duration = 1400
        const start = performance.now()
        const frame = t => {
          const p = Math.min(1, (t - start) / duration)
          const eased = 1 - Math.pow(1 - p, 3)
          el.textContent = Math.round(target * eased)
          if (p < 1) requestAnimationFrame(frame)
        }
        requestAnimationFrame(frame)
        el.dataset.countDone = '1'
        counterObserver.unobserve(el)
      })
    },
    { threshold: 0.4 },
  )
  els.forEach(el => counterObserver.observe(el))
}

// ---------------------------------------------------------------------
// Hero split-and-settle. Walks the matched H1s, replaces each character
// with a span, and lets a CSS keyframe animation drop them in with a
// short stagger. Mixed content (<em>, <i>, <br>) is preserved.
//
// If the title sits inside a `.reveal` ancestor we wait for that ancestor
// to gain `.in` before kicking off the per-char animation, so we don't
// fight the parent's opacity fade.
// ---------------------------------------------------------------------

const SPLIT_HERO_SELECTORS = [
  '.hero-2026__title',
  '.contact-page-2026__title',
  '.svc-detail__title',
  '.case-study__title',
  '.journal-article__title',
]

const splitElement = el => {
  if (el.dataset.splitDone) return
  el.dataset.splitDone = '1'
  // innerText preserves `<br>` as newlines; collapse to a clean aria-label
  // so screen readers get the natural reading instead of "JSDevelopment".
  const label = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim()
  el.setAttribute('aria-label', label)

  const counter = { i: 0 }

  const wrapTextNode = node => {
    const text = node.textContent
    if (!text) return
    const fragment = document.createDocumentFragment()
    // Group consecutive non-space chars into a `.split-text__word` wrapper
    // so the per-character spans inside can't wrap mid-word. Whitespace
    // between words is preserved as plain text so the H1 still soft-wraps
    // at word boundaries when needed. NBSP (U+00A0) intentionally treated
    // as a non-breaker so surrounding text stays joined.
    const isSpace = ch => ch !== ' ' && /\s/.test(ch)
    let word = null
    const flushWord = () => {
      if (word) {
        fragment.appendChild(word)
        word = null
      }
    }
    for (const ch of text) {
      if (isSpace(ch)) {
        flushWord()
        fragment.appendChild(document.createTextNode(ch === '\n' ? ' ' : ch))
        continue
      }
      if (!word) {
        word = document.createElement('span')
        word.className = 'split-text__word'
        word.setAttribute('aria-hidden', 'true')
      }
      const span = document.createElement('span')
      span.className = 'split-text__char'
      span.style.setProperty('--i', counter.i++)
      span.textContent = ch
      word.appendChild(span)
    }
    flushWord()
    node.parentNode.replaceChild(fragment, node)
  }

  const walk = parent => {
    // Snapshot first — wrapping replaces nodes, which would mutate live NodeLists.
    const children = Array.from(parent.childNodes)
    for (const node of children) {
      if (node.nodeType === Node.TEXT_NODE) {
        wrapTextNode(node)
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'BR') {
        walk(node)
      }
    }
  }

  walk(el)

  const start = () => requestAnimationFrame(() => el.classList.add('is-split'))
  const reveal = el.closest('.reveal')
  if (reveal && !reveal.classList.contains('in')) {
    const obs = new MutationObserver(() => {
      if (reveal.classList.contains('in')) {
        obs.disconnect()
        start()
      }
    })
    obs.observe(reveal, { attributes: true, attributeFilter: ['class'] })
  } else {
    start()
  }
}

const setupSplitHeroes = () => {
  if (typeof window === 'undefined') return
  if (prefersReducedMotion()) return
  document
    .querySelectorAll(SPLIT_HERO_SELECTORS.join(','))
    .forEach(splitElement)
}

const run = () => {
  // RAF defer so we observe after React has flushed the new DOM.
  requestAnimationFrame(() => {
    setupReveal()
    setupCounters()
    setupSplitHeroes()
  })
}

export const onInitialClientRender = () => run()
export const onRouteUpdate = () => run()
